<template>
  <div class="space-y-6">
    <HermesCard title="Now" :meta="nowMeta">
      <template #actions>
        <span v-if="now" class="text-gray-500">updated {{ relative(now.at) }}</span>
      </template>
      <p v-if="nowError" class="text-red-600">{{ nowError }}</p>
      <div v-else-if="now" class="grid gap-6 md:grid-cols-3">
        <div>
          <h3 class="mb-2 font-medium text-gray-700">Attention</h3>
          <p v-if="!attention.length" class="text-gray-500">Nothing stuck.</p>
          <ul class="space-y-2">
            <li v-for="a in attention" :key="a.key" class="rounded border border-red-200 bg-red-tint px-3 py-2">
              <div class="font-medium text-gray-900">{{ a.label }}</div>
              <div class="text-gray-700">
                <span class="sensitive">{{ a.who }}</span> · <span class="sensitive">{{ a.tenant }}</span>
                · <a :href="`/agent/${a.session_id}`" target="_blank" rel="noopener" class="underline">session</a>
              </div>
            </li>
          </ul>
        </div>
        <div class="md:col-span-2">
          <h3 class="mb-2 font-medium text-gray-700">Live workers</h3>
          <p v-if="!now.workers.length" class="text-gray-500">No worker is running.</p>
          <table v-else class="w-full">
            <thead><tr><th :class="TH">User</th><th :class="TH">Tenant</th><th :class="TH">State</th><th :class="TH">Up</th><th :class="TH">Browser</th></tr></thead>
            <tbody class="divide-y divide-gray-100">
              <tr v-for="w in now.workers" :key="w.id">
                <td :class="TD"><span class="sensitive">{{ w.user && w.user.name }}</span><span v-if="w.user && w.user.customer" :class="CUSTOMER">customer</span></td>
                <td :class="TD" class="sensitive">{{ w.tenant && w.tenant.name }}</td>
                <td :class="TD">{{ w.state }}</td>
                <td :class="TD">{{ seconds(w.started_age) }}</td>
                <td :class="TD">{{ w.browser_age == null ? '—' : `${seconds(w.browser_age)} ago` }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </HermesCard>

    <HermesCard title="Conversations" :meta="turnsMeta" flush>
      <template #actions>
        <select v-model.number="view.days" :class="SIZED_INPUT"><option :value="1">24 hours</option><option :value="7">7 days</option><option :value="14">14 days</option></select>
        <select v-model="view.audience" :class="SIZED_INPUT"><option value="all">Everyone</option><option value="customers">Customers</option><option value="staff">Staff</option></select>
        <select v-model="view.tenant" :class="SIZED_INPUT"><option value="">All tenants</option><option v-for="t in tenants" :key="t.id" :value="String(t.id)">{{ t.name }}</option></select>
        <select v-model="view.outcome" :class="SIZED_INPUT"><option value="">Any outcome</option><option v-for="o in OUTCOMES" :key="o" :value="o">{{ words(o) }}</option></select>
        <label class="inline-flex items-center gap-2 text-gray-700"><input v-model="view.problems" type="checkbox"> Problems only</label>
        <button type="button" :class="SECONDARY_BUTTON" :disabled="turnsLoading" @click="loadTurns">Refresh</button>
      </template>
      <LoadingIndicator v-if="turnsLoading && !turns.length" />
      <p v-else-if="turnsError" class="p-4 text-red-600">{{ turnsError }}</p>
      <p v-else-if="!conversations.length" class="p-4 text-gray-500">No turns match.</p>
      <table v-else class="w-full">
        <thead class="border-b border-gray-200">
          <tr>
            <th :class="TH">When</th><th :class="TH">Who</th><th :class="TH">Asked</th><th :class="TH">Model · hats</th>
            <th :class="TH">Tools</th><th :class="TH">Time</th><th :class="TH">Outcome</th><th :class="TH">Cost</th>
          </tr>
        </thead>
        <tbody>
          <template v-for="c in conversations" :key="c.session_id">
            <tr class="cursor-pointer border-b border-gray-100 hover:bg-gray-50" :class="{ 'bg-gray-50': isOpen(c.session_id) }" @click="toggle(c.session_id)">
              <td :class="TD" class="whitespace-nowrap" :title="absolute(c.last_at)">
                <span class="inline-block w-4 text-gray-500">{{ isOpen(c.session_id) ? '▾' : '▸' }}</span>{{ relative(c.last_at) }}
              </td>
              <td :class="TD">
                <div><span class="sensitive">{{ c.user && c.user.name }}</span><span v-if="c.user && c.user.customer" :class="CUSTOMER">customer</span></div>
                <div class="sensitive text-gray-500">{{ c.tenant && c.tenant.name }}</div>
              </td>
              <td :class="TD" class="max-w-md">
                <p class="font-medium text-gray-900 sensitive line-clamp-1">{{ c.title || 'Untitled conversation' }}</p>
                <p class="text-gray-500">{{ c.turns.length }} turn{{ c.turns.length === 1 ? '' : 's' }} · <a :href="`/agent/${c.session_id}`" target="_blank" rel="noopener" class="underline" @click.stop>open session</a></p>
              </td>
              <td :class="TD">
                <div class="text-gray-700">{{ c.models.join(', ') || '—' }}</div>
                <div class="flex flex-wrap gap-1 mt-1"><span v-for="h in c.hats" :key="h" class="rounded bg-primary-50 px-1.5 text-[11px] text-gray-900">{{ words(h) }}</span></div>
              </td>
              <td :class="TD" class="tabular-nums">{{ c.tool_calls || '—' }}</td>
              <td :class="TD" class="whitespace-nowrap tabular-nums">{{ c.seconds ? seconds(c.seconds) : '—' }}</td>
              <td :class="TD">
                <div class="flex flex-wrap gap-1">
                  <span v-for="o in c.outcomes" :key="o.outcome" :class="['rounded px-2 py-0.5', OUTCOME_STYLE[o.outcome] || 'bg-gray-100 text-gray-800']">{{ o.count }} {{ words(o.outcome) }}</span>
                </div>
                <div class="flex flex-wrap gap-1 mt-1"><span v-for="f in c.flags" :key="f" class="rounded bg-red-tint px-1.5 text-[11px] text-gray-900">{{ words(f) }}</span></div>
              </td>
              <td :class="TD" class="tabular-nums">{{ money(c.cost_usd) }}</td>
            </tr>
            <template v-for="t in (isOpen(c.session_id) ? c.turns : [])" :key="t.run_id">
              <tr class="cursor-pointer border-b border-gray-100 bg-white hover:bg-gray-50" :class="{ 'bg-gray-50': view.turn === String(t.run_id) }" @click="view.turn = view.turn === String(t.run_id) ? '' : String(t.run_id)">
                <td :class="TD" class="whitespace-nowrap pl-10" :title="absolute(t.asked_at)">{{ relative(t.asked_at) }}</td>
                <td :class="TD" />
                <td :class="TD" class="max-w-md"><p class="sensitive line-clamp-2">{{ t.prompt }}</p></td>
                <td :class="TD">
                  <div class="text-gray-700">{{ t.model || '—' }}</div>
                  <div class="flex flex-wrap gap-1 mt-1"><span v-for="h in t.hats" :key="h" class="rounded bg-primary-50 px-1.5 text-[11px] text-gray-900">{{ words(h) }}</span></div>
                </td>
                <td :class="TD" class="tabular-nums">{{ t.tool_calls ?? '—' }}</td>
                <td :class="TD" class="whitespace-nowrap tabular-nums">
                  <div>{{ t.seconds == null ? '—' : seconds(t.seconds) }}</div>
                  <div class="text-gray-500">{{ t.seconds_to_text == null ? 'no text' : `text at ${seconds(t.seconds_to_text)}` }}</div>
                </td>
                <td :class="TD">
                  <span :class="['rounded px-2 py-0.5', OUTCOME_STYLE[t.outcome] || 'bg-gray-100 text-gray-800']">{{ words(t.outcome) }}</span>
                  <div class="flex flex-wrap gap-1 mt-1">
                    <span v-if="t.stops" class="rounded bg-red-tint px-1.5 text-[11px] text-gray-900">{{ t.stops }} stop{{ t.stops === 1 ? '' : 's' }}</span>
                    <span v-for="f in t.flags" :key="f" class="rounded bg-red-tint px-1.5 text-[11px] text-gray-900">{{ words(f) }}</span>
                  </div>
                </td>
                <td :class="TD" class="tabular-nums">{{ t.cost_usd == null ? '—' : money(t.cost_usd) }}</td>
              </tr>
              <tr v-if="view.turn === String(t.run_id)">
                <td colspan="8" class="p-0"><ConsoleTurnDetail :run-id="t.run_id" /></td>
              </tr>
            </template>
          </template>
        </tbody>
      </table>
    </HermesCard>

    <HermesCard title="Usage" :meta="usageMeta" collapsible flush>
      <p v-if="usageError" class="p-4 text-red-600">{{ usageError }}</p>
      <table v-else-if="usage.length" class="w-full">
        <thead class="border-b border-gray-200">
          <tr><th :class="TH">Day</th><th :class="TH">Tenant</th><th :class="TH">Turns</th><th :class="TH">Users</th><th :class="TH">Customer turns</th><th :class="TH">Customer users</th><th :class="TH">Cost</th><th :class="TH">Unpriced turns</th></tr>
        </thead>
        <tbody class="divide-y divide-gray-100">
          <tr v-for="u in usage" :key="`${u.day}-${u.slug}`">
            <td :class="TD">{{ u.day }}</td>
            <td :class="TD" class="sensitive">{{ u.name }}</td>
            <td :class="TD" class="tabular-nums">{{ u.turns }}</td>
            <td :class="TD" class="tabular-nums">{{ u.users }}</td>
            <td :class="TD" class="tabular-nums">{{ u.customer_turns }}</td>
            <td :class="TD" class="tabular-nums">{{ u.customer_users }}</td>
            <td :class="TD" class="tabular-nums">{{ money(u.cost_usd) }}</td>
            <td :class="TD" class="tabular-nums">{{ u.unpriced_turns || '' }}</td>
          </tr>
        </tbody>
      </table>
    </HermesCard>
  </div>
</template>

<script>
import LoadingIndicator from '@/components/widgetcomponents/library/LoadingIndicator.vue';
import queryState from '@/mixins/queryState';
import HermesCard from '../HermesCard.vue';
import ConsoleTurnDetail from './ConsoleTurnDetail.vue';
import { API, SECONDARY_BUTTON, SIZED_INPUT, TD, TH, absolute, errorOf, money, relative, seconds, words } from '../hermes';

const OUTCOMES = ['answered', 'stopped', 'stopped_answered', 'no_reply', 'error', 'running'];
const OUTCOME_STYLE = {
  answered: 'bg-secondary-200 text-gray-900',
  stopped: 'bg-red-tint text-gray-900',
  stopped_answered: 'bg-neutral-100 text-gray-900',
  no_reply: 'bg-red-tint text-gray-900',
  error: 'bg-red-tint text-gray-900',
  running: 'bg-neutral-100 text-gray-900',
};
const NOW_POLL_MS = 15000;

export default {
  name: 'HermesConsole',
  components: { LoadingIndicator, HermesCard, ConsoleTurnDetail },
  mixins: [queryState('view', { days: 1, audience: 'all', tenant: '', outcome: '', problems: false, open: '', turn: '' })],
  emits: ['crumb', 'pending'],
  data() {
    return {
      now: null, nowError: '', turns: [], turnsLoading: false, turnsError: '', usage: [], usageError: '',
      truncated: false,
      OUTCOMES, OUTCOME_STYLE, SECONDARY_BUTTON, SIZED_INPUT, TD, TH,
      CUSTOMER: 'ml-2 rounded bg-primary-100 px-1.5 text-[11px] text-gray-900',
    };
  },
  computed: {
    attention() {
      if (!this.now) return [];
      const out = [];
      for (const s of this.now.busy_sessions) {
        if (!s.flags.length) continue;
        const why = s.flags.includes('no_worker') ? `Shows "${s.state}" but no worker is running` : `"${s.state}" with no events for ${seconds(s.quiet_seconds)}`;
        out.push({ key: `s-${s.session_id}`, label: why, who: s.user && s.user.name, tenant: s.tenant && s.tenant.name, session_id: s.session_id });
      }
      for (const st of this.now.unanswered_stops) {
        out.push({ key: `x-${st.session_id}`, label: `${st.count} Stop press${st.count === 1 ? '' : 'es'} not acted on for ${seconds(st.waiting_seconds)}`, who: '', tenant: '', session_id: st.session_id });
      }
      return out;
    },
    nowMeta() {
      if (!this.now) return '';
      return `${this.now.workers.length} worker${this.now.workers.length === 1 ? '' : 's'} live · ${this.attention.length} need attention`;
    },
    tenants() {
      const seen = new Map();
      for (const u of this.usage) seen.set(u.tenant_id, { id: u.tenant_id, name: u.name });
      for (const t of this.turns) if (t.tenant) seen.set(t.tenant.id, t.tenant);
      return [...seen.values()].sort((a, b) => a.name.localeCompare(b.name));
    },
    shown() {
      return this.view.problems ? this.turns.filter((t) => t.outcome !== 'answered' || t.flags.length) : this.turns;
    },
    conversations() {
      const groups = new Map();
      for (const t of this.shown) {
        if (!groups.has(t.session_id)) {
          groups.set(t.session_id, { session_id: t.session_id, title: t.session_title, user: t.user, tenant: t.tenant, turns: [] });
        }
        groups.get(t.session_id).turns.push(t);
      }
      return [...groups.values()].map((g) => {
        const turns = [...g.turns].sort((a, b) => new Date(a.asked_at) - new Date(b.asked_at));
        const counts = {};
        for (const t of turns) counts[t.outcome] = (counts[t.outcome] || 0) + 1;
        return {
          ...g,
          turns,
          last_at: turns[turns.length - 1].asked_at,
          models: [...new Set(turns.map((t) => t.model).filter(Boolean))],
          hats: [...new Set(turns.flatMap((t) => t.hats || []))],
          tool_calls: turns.reduce((n, t) => n + (t.tool_calls || 0), 0),
          seconds: turns.reduce((n, t) => n + (t.seconds || 0), 0),
          cost_usd: turns.reduce((n, t) => n + (t.cost_usd || 0), 0),
          outcomes: OUTCOMES.filter((o) => counts[o]).map((o) => ({ outcome: o, count: counts[o] })),
          flags: [...new Set(turns.flatMap((t) => [...(t.flags || []), ...(t.stops ? ['stopped'] : [])]))],
        };
      }).sort((a, b) => new Date(b.last_at) - new Date(a.last_at));
    },
    openIds() {
      return new Set(String(this.view.open || '').split(',').filter(Boolean));
    },
    turnsMeta() {
      const bad = this.turns.filter((t) => t.outcome !== 'answered').length;
      const cost = this.turns.reduce((n, t) => n + (t.cost_usd || 0), 0);
      return `${this.conversations.length} conversations · ${this.turns.length}${this.truncated ? '+' : ''} turns · ${bad} not answered · ${money(cost)} recorded`;
    },
    usageMeta() {
      const cost = this.usage.reduce((n, u) => n + (u.cost_usd || 0), 0);
      return `last 14 days · ${money(cost)} recorded`;
    },
  },
  watch: {
    'view.days'() { this.loadTurns(); },
    'view.audience'() { this.loadTurns(); },
    'view.tenant'() { this.loadTurns(); },
    'view.outcome'() { this.loadTurns(); },
  },
  mounted() {
    this.loadNow();
    this.loadTurns();
    this.loadUsage();
    this.timer = setInterval(this.loadNow, NOW_POLL_MS);
  },
  beforeUnmount() {
    clearInterval(this.timer);
  },
  methods: {
    absolute, money, relative, seconds, words,
    isOpen(sessionId) {
      return this.openIds.has(String(sessionId));
    },
    toggle(sessionId) {
      const ids = new Set(this.openIds);
      const key = String(sessionId);
      if (ids.has(key)) ids.delete(key);
      else ids.add(key);
      this.view.open = [...ids].join(',');
    },
    async loadNow() {
      try {
        const { data } = await this.$axios.get(`${API}/console/now/`);
        this.now = data;
        this.nowError = '';
      } catch (e) {
        this.nowError = errorOf(e, 'Live state could not be loaded.');
      }
    },
    async loadTurns() {
      this.turnsLoading = true;
      this.turnsError = '';
      try {
        const { data } = await this.$axios.get(`${API}/console/turns/`, {
          params: { days: this.view.days, audience: this.view.audience, tenant: this.view.tenant || undefined, outcome: this.view.outcome || undefined },
        });
        this.turns = data.turns;
        this.truncated = data.truncated;
        const selected = this.turns.find((t) => String(t.run_id) === String(this.view.turn));
        if (selected && !this.isOpen(selected.session_id)) this.toggle(selected.session_id);
      } catch (e) {
        this.turnsError = errorOf(e, 'Turns could not be loaded.');
      } finally {
        this.turnsLoading = false;
      }
    },
    async loadUsage() {
      try {
        const { data } = await this.$axios.get(`${API}/console/usage/`, { params: { days: 14 } });
        this.usage = data.rows;
      } catch (e) {
        this.usageError = errorOf(e, 'Usage could not be loaded.');
      }
    },
  },
};
</script>
